// Driver deductions: one place that decides what a pay week's deductions are,
// so the weekly payout screen, the driver's commission summary and the bank
// file all agree -- and a reducing balance genuinely reduces.
//
// driver_financial_adjustments is the *charge* (the setup). The ledger
// (driver_deduction_transactions, migration 0118) is the history of what was
// actually taken. A week's deductions come from the ledger once that week has
// been recorded (so a past summary never changes when the charge is later
// edited), and are projected from the charge until then.
//
// The pure functions at the top have no database access and are unit tested
// (driverDeductions.test.ts). The database is the final guard either way:
// record_week_deductions() caps at the remaining balance and can't deduct a
// week twice.
import { supabase } from "@/lib/supabase";

export type AdjustmentMode = "one_off" | "recurring" | "reducing_balance";
export type DeductionStatus = "active" | "paid" | "cancelled" | "adjusted";
export type LedgerTxnType = "charge" | "deduction" | "reversal" | "adjustment" | "cancellation";

type Numeric = number | string | null | undefined;

export interface AdjustmentRow {
  id: string;
  driver_name: string;
  adjustment_type: "deduction" | "credit";
  category: string | null;
  amount: Numeric;
  total_amount: Numeric;
  remaining_balance: Numeric;
  adjustment_mode: AdjustmentMode;
  description: string | null;
  active: boolean;
  deduction_status?: DeductionStatus | null;
  starts_on: string | null;
  ends_on: string | null;
  bank_account_number?: string | null;
  bank_account_name?: string | null;
  payment_reference?: string | null;
  /** Recurring charges only: take a share of the weekly amount when the charge starts or ends part-way through a pay week. */
  prorate_partial_weeks?: boolean | null;
}

export interface LedgerRow {
  id: string;
  adjustment_id: string | null;
  driver_name: string;
  txn_type: LedgerTxnType;
  txn_date: string;
  week_start: string | null;
  week_end: string | null;
  amount: Numeric;
  balance_delta: Numeric;
  balance_after: Numeric;
  description_snapshot: string | null;
  category_snapshot: string | null;
  original_amount_snapshot: Numeric;
  reverses_txn_id: string | null;
  reversed_at: string | null;
  note: string | null;
  source: string;
  created_at: string;
}

export interface DeductionLine {
  adjustmentId: string;
  driverName: string;
  /** 'insurance' is the flat weekly excess-fund contribution, tracked in its own ledger. */
  kind: "deduction" | "credit" | "insurance";
  category: string | null;
  /** The short description (about 50 chars) shown on the commission summary. */
  label: string;
  mode: AdjustmentMode;
  thisPay: number;
  originalAmount: number | null;
  paidToDate: number | null;
  remaining: number | null;
  /** Already written to the ledger for this pay week. */
  recorded: boolean;
  /** Pre-formatted "Original $1,000.00 · Paid to date $50.00 · Remaining $950.00" for balance charges. */
  detail: string | null;
  adjustment: AdjustmentRow;
}

export const SHORT_DESCRIPTION_LENGTH = 50;

export const num = (v: Numeric): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

export const money = (n: number): string => {
  const v = round2(n);
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** About 50 characters, cut on a word where it can, with an ellipsis if shortened. */
export function shortDescription(text: string | null | undefined, max = SHORT_DESCRIPTION_LENGTH): string {
  const t = (text ?? "").replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(" ");
  const base = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${base.trimEnd()}…`;
}

/** A deduction can never take more than is left. */
export function capDeduction(amount: number, remaining: number | null): { applied: number; capped: boolean } {
  const want = Math.max(0, round2(amount));
  if (remaining === null) return { applied: want, capped: false };
  const left = Math.max(0, round2(remaining));
  const applied = Math.min(want, left);
  return { applied, capped: applied < want };
}

const DAY_MS = 86_400_000;
const dayNumber = (iso: string): number => Math.round(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) / DAY_MS);

/** How many days of the pay week (inclusive) the charge is live: a charge starting Thursday of a Mon-Sun week is live for 4. */
export function activeDaysInWeek(
  a: Pick<AdjustmentRow, "starts_on" | "ends_on">,
  weekStart: string,
  weekEnd: string
): { days: number; weekDays: number } {
  const ws = dayNumber(weekStart);
  const we = dayNumber(weekEnd);
  const from = Math.max(ws, a.starts_on ? dayNumber(a.starts_on) : ws);
  const to = Math.min(we, a.ends_on ? dayNumber(a.ends_on) : we);
  return { days: Math.max(0, to - from + 1), weekDays: we - ws + 1 };
}

/** The weekly amount scaled to the days the charge is live that week (4 of 7 days = 4/7 of the amount). */
export function prorateWeeklyAmount(weekly: number, days: number, weekDays: number): number {
  if (weekDays <= 0) return round2(weekly);
  return round2((weekly * Math.min(days, weekDays)) / weekDays);
}

/** The same overlap rule the weekly payout has always used. */
export function overlapsWeek(a: Pick<AdjustmentRow, "starts_on" | "ends_on">, weekStart: string, weekEnd: string): boolean {
  if (a.starts_on && a.starts_on > weekEnd) return false;
  if (a.ends_on && a.ends_on < weekStart) return false;
  return true;
}

const isLiveDeduction = (t: LedgerRow) => t.txn_type === "deduction" && !t.reversed_at;

function formatDetail(original: number | null, paid: number | null, remaining: number | null): string | null {
  if (original === null || paid === null || remaining === null) return null;
  return `Original ${money(original)} · Paid to date ${money(paid)} · Remaining ${money(remaining)}`;
}

export function buildWeekDeductionLines(args: {
  weekStart: string;
  weekEnd: string;
  adjustments: AdjustmentRow[];
  /** Ledger rows for these charges (live deductions are all that's used). */
  ledger: LedgerRow[];
  /** Company-wide weekly insurance excess, which overrides an 'insurance' charge's own amount when set. */
  insuranceWeekly: number;
  labelFor: (category: string | null) => string;
}): DeductionLine[] {
  const { weekStart, weekEnd, adjustments, ledger, insuranceWeekly, labelFor } = args;

  const liveByAdj = new Map<string, LedgerRow[]>();
  for (const t of ledger) {
    if (!t.adjustment_id || !isLiveDeduction(t)) continue;
    const list = liveByAdj.get(t.adjustment_id) ?? [];
    list.push(t);
    liveByAdj.set(t.adjustment_id, list);
  }

  const lines: DeductionLine[] = [];

  for (const a of adjustments) {
    const live = liveByAdj.get(a.id) ?? [];
    const thisWeek = live.find((t) => t.week_start === weekStart) ?? null;
    const isCredit = a.adjustment_type === "credit";
    const isInsurance = a.category === "insurance" && !isCredit;

    // A week already in the ledger always shows what was actually taken,
    // even if the charge has since been paid off, edited or switched off.
    if (!thisWeek) {
      if (!a.active) continue;
      if (a.deduction_status === "paid" || a.deduction_status === "cancelled") continue;
      if (!overlapsWeek(a, weekStart, weekEnd)) continue;
    }

    const baseAmount = num(a.amount);
    let thisPay = baseAmount;

    // A recurring charge that starts or stops part-way through the week (a lease starting
    // on a Thursday) takes only its share of the weekly amount, if it is set to pro-rate.
    let prorateSuffix = "";
    if (a.adjustment_mode === "recurring" && a.prorate_partial_weeks && !isInsurance) {
      const { days, weekDays } = activeDaysInWeek(a, weekStart, weekEnd);
      if (days > 0 && days < weekDays) {
        prorateSuffix = ` (${days} of ${weekDays} days)`;
        thisPay = prorateWeeklyAmount(baseAmount, days, weekDays);
      }
    }
    let originalAmount: number | null = null;
    let paidToDate: number | null = null;
    let remaining: number | null = null;

    if (isInsurance) {
      thisPay = insuranceWeekly > 0 ? insuranceWeekly : baseAmount;
    } else if (thisWeek) {
      thisPay = num(thisWeek.amount);
    }

    if (a.adjustment_mode === "reducing_balance" && !isCredit) {
      const total = a.total_amount === null || a.total_amount === undefined ? null : num(a.total_amount);
      const currentRemaining =
        a.remaining_balance === null || a.remaining_balance === undefined ? total : num(a.remaining_balance);
      originalAmount = total ?? (thisWeek?.original_amount_snapshot != null ? num(thisWeek.original_amount_snapshot) : null);

      if (thisWeek) {
        remaining = thisWeek.balance_after === null ? currentRemaining : num(thisWeek.balance_after);
        paidToDate = round2(
          live.filter((t) => (t.week_start ?? "") <= weekStart).reduce((s, t) => s + num(t.amount), 0)
        );
      } else {
        const capped = capDeduction(thisPay, currentRemaining);
        if (currentRemaining !== null && capped.applied <= 0) continue; // nothing left to take
        thisPay = capped.applied;
        remaining = currentRemaining === null ? null : round2(currentRemaining - thisPay);
        paidToDate = round2(live.reduce((s, t) => s + num(t.amount), 0) + thisPay);
      }
    }

    if (thisPay <= 0) continue;

    lines.push({
      adjustmentId: a.id,
      driverName: a.driver_name,
      kind: isCredit ? "credit" : isInsurance ? "insurance" : "deduction",
      category: a.category,
      label: isInsurance ? labelFor(a.category) : `${shortDescription(a.description) || labelFor(a.category)}${prorateSuffix}`,
      mode: a.adjustment_mode,
      thisPay: round2(thisPay),
      originalAmount,
      paidToDate,
      remaining,
      recorded: !!thisWeek,
      detail: formatDetail(originalAmount, paidToDate, remaining),
      adjustment: a,
    });
  }

  return lines;
}

// ---------------------------------------------------------------------------
// Loading (database)
// ---------------------------------------------------------------------------

const ADJUSTMENT_COLUMNS =
  "id, driver_name, adjustment_type, category, amount, total_amount, remaining_balance, adjustment_mode, description, active, deduction_status, starts_on, ends_on, bank_account_number, bank_account_name, payment_reference, prorate_partial_weeks";

const LEDGER_COLUMNS =
  "id, adjustment_id, driver_name, txn_type, txn_date, week_start, week_end, amount, balance_delta, balance_after, description_snapshot, category_snapshot, original_amount_snapshot, reverses_txn_id, reversed_at, note, source, created_at";

export async function loadWeekDeductionLines(args: {
  weekStart: string;
  weekEnd: string;
  driverName?: string;
  insuranceWeekly: number;
  labelFor: (category: string | null) => string;
}): Promise<DeductionLine[]> {
  const { weekStart, weekEnd, driverName } = args;

  let activeQuery = supabase
    .from("driver_financial_adjustments")
    .select(ADJUSTMENT_COLUMNS)
    .eq("active", true)
    .lte("starts_on", weekEnd)
    .or(`ends_on.is.null,ends_on.gte.${weekStart}`);
  if (driverName) activeQuery = activeQuery.eq("driver_name", driverName);

  let recordedQuery = supabase
    .from("driver_deduction_transactions")
    .select(LEDGER_COLUMNS)
    .eq("txn_type", "deduction")
    .eq("week_start", weekStart)
    .is("reversed_at", null);
  if (driverName) recordedQuery = recordedQuery.eq("driver_name", driverName);

  const [{ data: activeRows, error: activeErr }, { data: recordedRows, error: recordedErr }] = await Promise.all([
    activeQuery,
    recordedQuery,
  ]);
  if (activeErr) throw activeErr;
  if (recordedErr) throw recordedErr;

  const adjustments = new Map<string, AdjustmentRow>();
  (activeRows as AdjustmentRow[] | null)?.forEach((a) => adjustments.set(a.id, a));

  // Charges already recorded for this week but no longer active (paid off, switched off).
  const missingIds = ((recordedRows as LedgerRow[] | null) ?? [])
    .map((t) => t.adjustment_id)
    .filter((id): id is string => !!id && !adjustments.has(id));
  if (missingIds.length > 0) {
    const { data: extra, error: extraErr } = await supabase
      .from("driver_financial_adjustments")
      .select(ADJUSTMENT_COLUMNS)
      .in("id", missingIds);
    if (extraErr) throw extraErr;
    (extra as AdjustmentRow[] | null)?.forEach((a) => adjustments.set(a.id, a));
  }

  const ids = [...adjustments.keys()];
  let ledger: LedgerRow[] = [];
  if (ids.length > 0) {
    const { data: ledgerRows, error: ledgerErr } = await supabase
      .from("driver_deduction_transactions")
      .select(LEDGER_COLUMNS)
      .eq("txn_type", "deduction")
      .is("reversed_at", null)
      .in("adjustment_id", ids);
    if (ledgerErr) throw ledgerErr;
    ledger = (ledgerRows as LedgerRow[] | null) ?? [];
  }

  return buildWeekDeductionLines({
    weekStart,
    weekEnd,
    adjustments: [...adjustments.values()],
    ledger,
    insuranceWeekly: args.insuranceWeekly,
    labelFor: args.labelFor,
  });
}

// ---------------------------------------------------------------------------
// Writing (all through database functions that check the caller and the rules)
// ---------------------------------------------------------------------------

export interface RecordResult {
  recorded: number;
  skipped: number;
  capped: { adjustment_id: string; driver_name: string; requested: number; applied: number }[];
  completed: string[];
}

/** Records the lines for a pay week. Safe to call again for the same week -- it never deducts twice. */
export async function recordWeekDeductions(args: {
  weekStart: string;
  weekEnd: string;
  txnDate?: string;
  lines: DeductionLine[];
}): Promise<RecordResult> {
  const payload = args.lines
    .filter((l) => l.kind === "deduction" && !l.recorded && l.thisPay > 0)
    .map((l) => ({ adjustment_id: l.adjustmentId, amount: l.thisPay }));
  if (payload.length === 0) return { recorded: 0, skipped: 0, capped: [], completed: [] };

  const { data, error } = await supabase.rpc("record_week_deductions", {
    p_week_start: args.weekStart,
    p_week_end: args.weekEnd,
    p_txn_date: args.txnDate ?? args.weekEnd,
    p_lines: payload,
  });
  if (error) throw error;
  return data as RecordResult;
}

export async function reverseDeduction(txnId: string, note: string): Promise<void> {
  const { error } = await supabase.rpc("reverse_driver_deduction", { p_txn_id: txnId, p_note: note });
  if (error) throw error;
}

export async function adjustDeductionBalance(adjustmentId: string, newBalance: number, note: string): Promise<void> {
  const { error } = await supabase.rpc("adjust_driver_deduction_balance", {
    p_adjustment_id: adjustmentId,
    p_new_balance: newBalance,
    p_note: note,
  });
  if (error) throw error;
}

export async function cancelDeduction(adjustmentId: string, note: string): Promise<void> {
  const { error } = await supabase.rpc("cancel_driver_deduction", { p_adjustment_id: adjustmentId, p_note: note });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Admin report
// ---------------------------------------------------------------------------

export interface ReportCharge {
  key: string;
  adjustmentId: string | null;
  driverName: string;
  description: string;
  category: string | null;
  mode: AdjustmentMode | null;
  status: DeductionStatus | "removed";
  chargeDate: string | null;
  originalAmount: number | null;
  /** Net taken within the period: deductions minus reversals. */
  deductedInPeriod: number;
  paidToDate: number;
  remaining: number | null;
  rows: LedgerRow[];
}

export interface ReportDriverTotals {
  driverName: string;
  charged: number;
  deducted: number;
  remaining: number;
}

export interface DeductionReport {
  charges: ReportCharge[];
  totals: ReportDriverTotals[];
}

export function buildDeductionReport(args: {
  /** Every ledger row for the chosen drivers -- not date-limited, so paid-to-date is whole-history. */
  ledger: LedgerRow[];
  adjustments: AdjustmentRow[];
  from: string;
  to: string;
}): DeductionReport {
  const { ledger, adjustments, from, to } = args;
  const adjById = new Map(adjustments.map((a) => [a.id, a]));

  const groups = new Map<string, LedgerRow[]>();
  for (const t of ledger) {
    // Rows whose charge was deleted keep their history, grouped by what they say they were for.
    const key = t.adjustment_id ?? `removed|${t.driver_name}|${t.description_snapshot ?? ""}`;
    const list = groups.get(key) ?? [];
    list.push(t);
    groups.set(key, list);
  }

  const inRange = (d: string) => d >= from && d <= to;
  const charges: ReportCharge[] = [];

  for (const [key, all] of groups) {
    const rows = all
      .filter((t) => inRange(t.txn_date))
      .sort((x, y) => (x.txn_date === y.txn_date ? x.created_at.localeCompare(y.created_at) : x.txn_date.localeCompare(y.txn_date)));
    if (rows.length === 0) continue;

    const first = all[0];
    const adj = first.adjustment_id ? adjById.get(first.adjustment_id) ?? null : null;
    const chargeRow = all.find((t) => t.txn_type === "charge") ?? null;

    const sum = (list: LedgerRow[], type: LedgerTxnType) =>
      list.filter((t) => t.txn_type === type).reduce((s, t) => s + num(t.amount), 0);

    const liveDeductionsAll = all.filter(isLiveDeduction).reduce((s, t) => s + num(t.amount), 0);
    const original =
      adj?.total_amount != null
        ? num(adj.total_amount)
        : chargeRow?.original_amount_snapshot != null
        ? num(chargeRow.original_amount_snapshot)
        : first.original_amount_snapshot != null
        ? num(first.original_amount_snapshot)
        : null;

    const lastWithBalance = [...all].reverse().find((t) => t.balance_after !== null);
    const remaining = adj
      ? adj.adjustment_mode === "reducing_balance"
        ? num(adj.remaining_balance ?? adj.total_amount)
        : null
      : lastWithBalance
      ? num(lastWithBalance.balance_after)
      : null;

    charges.push({
      key,
      adjustmentId: first.adjustment_id,
      driverName: first.driver_name,
      description: shortDescription(adj?.description ?? first.description_snapshot ?? first.category_snapshot ?? "Deduction"),
      category: adj?.category ?? first.category_snapshot,
      mode: adj?.adjustment_mode ?? (chargeRow ? "reducing_balance" : null),
      status: adj ? adj.deduction_status ?? "active" : "removed",
      chargeDate: chargeRow?.txn_date ?? null,
      originalAmount: original,
      deductedInPeriod: round2(sum(rows, "deduction") - sum(rows, "reversal")),
      paidToDate: round2(liveDeductionsAll),
      remaining,
      rows,
    });
  }

  charges.sort((a, b) => a.driverName.localeCompare(b.driverName) || a.description.localeCompare(b.description));

  const totalsMap = new Map<string, ReportDriverTotals>();
  for (const c of charges) {
    const t = totalsMap.get(c.driverName) ?? { driverName: c.driverName, charged: 0, deducted: 0, remaining: 0 };
    t.charged += c.rows.filter((r) => r.txn_type === "charge").reduce((s, r) => s + num(r.amount), 0);
    t.deducted += c.deductedInPeriod;
    t.remaining += c.remaining ?? 0;
    totalsMap.set(c.driverName, t);
  }
  const totals = [...totalsMap.values()].map((t) => ({
    ...t,
    charged: round2(t.charged),
    deducted: round2(t.deducted),
    remaining: round2(t.remaining),
  }));

  return { charges, totals };
}

export async function loadDeductionReport(args: { driverNames: string[]; from: string; to: string }): Promise<DeductionReport> {
  let ledgerQuery = supabase.from("driver_deduction_transactions").select(LEDGER_COLUMNS).order("txn_date");
  if (args.driverNames.length > 0) ledgerQuery = ledgerQuery.in("driver_name", args.driverNames);
  const { data: ledgerRows, error } = await ledgerQuery;
  if (error) throw error;
  const ledger = (ledgerRows as LedgerRow[] | null) ?? [];

  const ids = [...new Set(ledger.map((t) => t.adjustment_id).filter((id): id is string => !!id))];
  let adjustments: AdjustmentRow[] = [];
  if (ids.length > 0) {
    const { data: adjRows, error: adjErr } = await supabase
      .from("driver_financial_adjustments")
      .select(ADJUSTMENT_COLUMNS)
      .in("id", ids);
    if (adjErr) throw adjErr;
    adjustments = (adjRows as AdjustmentRow[] | null) ?? [];
  }

  return buildDeductionReport({ ledger, adjustments, from: args.from, to: args.to });
}

const csvCell = (v: unknown): string => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function deductionReportToCsv(report: DeductionReport, from: string, to: string): string {
  const header = [
    "Driver", "Description", "Category", "Status", "Charge date", "Original amount",
    "Date", "Type", "Pay week", "Amount", "Balance after", "Note", "Paid to date", "Remaining",
  ];
  const lines: string[] = [`# Driver deductions report, ${from} to ${to}`, header.join(",")];
  for (const c of report.charges) {
    for (const r of c.rows) {
      lines.push(
        [
          c.driverName, c.description, c.category ?? "", c.status, c.chargeDate ?? "",
          c.originalAmount === null ? "" : c.originalAmount.toFixed(2),
          r.txn_date, r.txn_type, r.week_start ? `${r.week_start} to ${r.week_end ?? ""}` : "",
          num(r.amount).toFixed(2), r.balance_after === null ? "" : num(r.balance_after).toFixed(2),
          r.note ?? "", c.paidToDate.toFixed(2), c.remaining === null ? "" : c.remaining.toFixed(2),
        ].map(csvCell).join(",")
      );
    }
  }
  return lines.join("\n");
}
